// Auth types
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

// User types
export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  employee_id?: string;
  position?: string;
  hire_date?: string;
  birth_date?: string;
  phone?: string;
  is_active: boolean;
  last_login?: string;
  created_at: string;
  updated_at: string;
  roles?: Role[];  // Changed from role to roles (array)
  departments?: Department[];
  teams?: Team[];  // User can belong to multiple teams
  managers?: User[];
  subordinates?: User[];
  managed_teams?: Team[];
  supervised_departments?: Department[];
}

// Role types
export interface Role {
  id: number;
  name: string;
  description?: string;
  isSystem: boolean;
  permissions?: Permission[];
}

export interface Permission {
  id: number;
  name: string;
  resource: string;
  action: string;
  description?: string;
}

// Department & Team types
export interface Department {
  id: number;
  name: string;
  code: string;
  description?: string;
  managerId?: number;
  supervisorId?: number;
  manager?: User;
  supervisor?: User;
}

export interface Team {
  id: number;
  name: string;
  description?: string;
  departmentId: number;
  managerId?: number;
  leaderId?: number;
  department?: Department;
  manager?: User;
  leader?: User;
}

// Leave types
export interface LeaveType {
  id: number;
  name: string;
  code: string;
  description?: string;
  defaultDays: number;
  minDays: number;
  maxDays: number;
  isActive: boolean;
  requiresApproval: boolean;
  requiresDocument: boolean;
  advanceNoticeDays: number;
}

export interface LeaveRequest {
  id: string;
  userId: string;
  leaveTypeId: number;
  startDate: string;
  endDate: string;
  reason?: string;
  status: LeaveRequestStatus;
  totalDays: number;
  workingDays: number;
  substituteId?: string;
  emergencyContact?: string;
  emergencyPhone?: string;
  handoverNotes?: string;
  createdAt: string;
  updatedAt: string;
  user?: User;
  leaveType?: LeaveType;
  substitute?: User;
  approvalSteps?: ApprovalStep[];
  substitution?: Substitution;
}

export const LeaveRequestStatus = {
  DRAFT: 'draft' as const,
  PENDING: 'pending' as const,
  APPROVED: 'approved' as const,
  REJECTED: 'rejected' as const,
  CANCELLED: 'cancelled' as const,
} as const;

export type LeaveRequestStatus = typeof LeaveRequestStatus[keyof typeof LeaveRequestStatus];

export interface LeaveBalance {
  id: number;
  userId: string;
  leaveTypeId: number;
  year: number;
  initialBalance: number;
  adjustments: number;
  used: number;
  remaining: number;
  carriedOver: number;
  expiresAt?: string;
  user?: User;
  leaveType?: LeaveType;
}

// Approval types
export interface ApprovalStep {
  id: number;
  leaveRequestId: string;
  workflowStepId?: number;
  approverId?: string;
  status: ApprovalStatus;
  comment?: string;
  processedAt?: string;
  stepOrder: number;
  approver?: User;
}

export const ApprovalStatus = {
  PENDING: 'pending' as const,
  APPROVED: 'approved' as const,
  REJECTED: 'rejected' as const,
  SKIPPED: 'skipped' as const,
} as const;

export type ApprovalStatus = typeof ApprovalStatus[keyof typeof ApprovalStatus];

// Substitution types
export interface Substitution {
  id: number;
  leaveRequestId: string;
  userId: string;
  substituteId: string;
  status: SubstitutionStatus;
  responseComment?: string;
  responseDate?: string;
  user?: User;
  substitute?: User;
}

export const SubstitutionStatus = {
  PENDING: 'pending' as const,
  ACCEPTED: 'accepted' as const,
  REJECTED: 'rejected' as const,
} as const;

export type SubstitutionStatus = typeof SubstitutionStatus[keyof typeof SubstitutionStatus];

// Workflow types
export interface Workflow {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
  leaveTypeId?: number;
  steps?: WorkflowStep[];
  createdAt?: string;
  updatedAt?: string;
}

export interface WorkflowStep {
  id: number;
  name: string;
  description?: string;
  order: number;
  approverType: 'user' | 'role' | 'manager';
  approverId?: number;
  workflowId?: number;
}

// Common API response types
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ApiError {
  success: false;
  message: string;
  errors?: Record<string, string[]>;
}